import React from 'react';
import { SetupState } from '../types';
import { themePresets } from '../data/themes';
import { 
  Building, 
  MapPin, 
  Users, 
  Palette, 
  Image, 
  Zap, 
  Droplet, 
  ShieldCheck, 
  CreditCard, 
  UserCircle, 
  Gift, 
  AlertOctagon,
  Settings2,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';

interface SummaryStepProps {
  state: SetupState;
  currentUser?: any;
  onChange: (updates: Partial<SetupState>) => void;
  errors: Record<string, string>;
}

export const SummaryStep: React.FC<SummaryStepProps> = ({ state, currentUser, onChange, errors }) => {
  const activeColorTheme = themePresets.find(t => t.id === state.themeId) || themePresets[0];

  const getDormTypeLabel = (type: string) => {
    switch (type) {
      case 'mix': return 'หอพักรวม (ชาย/หญิง)';
      case 'male': return 'หอพักชาย';
      case 'female': return 'หอพักหญิง';
      default: return 'ไม่ระบุ';
    }
  };

  const getWaterLabel = () => {
    const rate = Math.max(0, parseFloat(state.waterRatePrice) || 0);
    return state.waterRateType === 'per_person' 
      ? `คิดรายคน: ${rate} บาท/คน` 
      : `คิดรายห้อง: ${rate} บาท/ห้อง`;
  };

  const formattedPromptPay = (num: string) => {
    if (!num) return '-';
    const cleanNum = num.replace(/\D/g, '');
    if (cleanNum.length === 10) {
      return `${cleanNum.slice(0,3)}-${cleanNum.slice(3,6)}-${cleanNum.slice(6)}`;
    } else if (cleanNum.length === 13) {
      return `${cleanNum.slice(0,1)}-${cleanNum.slice(1,5)}-${cleanNum.slice(5,10)}-${cleanNum.slice(10,12)}-${cleanNum.slice(12)}`;
    }
    return num;
  };

  const formattedAccountName = (name: string) => {
    if (!name) return '';
    return name.trim();
  };

  const getMaskedEmail = (email?: string) => {
    if (!email) return 'ไม่พบข้อมูลอีเมล';
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const name = parts[0];
    const domain = parts[1];
    if (name.length <= 2) return `${name}***@${domain}`;
    return `${name.substring(0, 2)}***@${domain}`;
  };

  return (
    <div className="space-y-6 font-sans">
      
      {/* Title block */}
      <div className="border-b border-slate-100 pb-4">
        <h2 className="text-xl font-sans font-bold text-slate-800 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-sm border border-amber-100 shrink-0 select-none">
            3
          </span>
          <span>ตรวจสอบ</span>
        </h2>
        <p className="text-sm text-slate-500 mt-1 pl-10 leading-normal">
          โปรดตรวจสอบข้อมูลหอพักและจุดรับยอด PromptPay ด้านล่างก่อนส่งข้อมูลเพื่อสร้างบัญชี
        </p>
      </div>

      {/* Payment Information Warning Card */}
      <div className="bg-amber-50 border border-amber-300 text-amber-900 p-4 rounded-2xl flex items-start gap-3.5 shadow-sm">
        <AlertOctagon className="w-7 h-7 text-amber-600 shrink-0 mt-0.5" />
        <div className="space-y-1.5 text-xs md:text-sm">
          <h4 className="font-bold text-amber-900 flex items-center gap-1.5 text-sm">
            ⚠️ ตรวจสอบข้อมูลรับเงินให้ถูกต้อง
          </h4>
          <p className="leading-relaxed">
            เลข PromptPay และชื่อบัญชีผู้รับเงินต้องตรงกับบัญชีที่ใช้รับโอนจริง เนื่องจากระบบจะใช้ข้อมูลในส่วนนี้ตรวจสอบสลิปอัตโนมัติ เพื่อยืนยันว่าสลิปนั้นโอนเข้าบัญชีของหอพักคุณจริง
          </p>
          <p className="font-bold text-amber-800">
            แนะนำให้สแกน QR Code ในตัวอย่างบิลก่อน เพื่อเช็กว่าชื่อรับเงินถูกต้อง
          </p>
        </div>
      </div>

      {/* Review details Grid columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs font-sans">
        
        {/* Left Card: General Information */}
        <div className="bg-white border border-slate-150 rounded-2xl p-5 space-y-4 shadow-sm">
          <h4 className="font-bold text-slate-700 text-sm flex items-center gap-2 border-b border-slate-50 pb-2">
            <Building className="w-4.5 h-4.5 text-indigo-500" />
            ข้อมูลพื้นฐานหอพัก
          </h4>

          <div className="space-y-3 font-medium">
            <div className="flex justify-between items-center py-1">
              <span className="text-slate-400">ชื่อหอพัก:</span>
              <span className="text-slate-800 font-bold text-sm">{state.dormName || <span className="text-rose-500 text-xs">ยังไม่ระบุชื่อหอพัก</span>}</span>
            </div>
            
            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">จังหวัดที่ตั้ง:</span>
              <span className="text-slate-800 font-semibold">{state.province || <span className="text-rose-500 text-xs font-bold">ยังไม่ระบุจังหวัด</span>}</span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">ประเภทหอพัก:</span>
              <span className="text-slate-800 font-semibold">{getDormTypeLabel(state.dormType)}</span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">จำนวนตึกที่บันทึก:</span>
              <span className="text-indigo-650 font-bold">{state.buildingsList.length || 1} ตึก</span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">จำนวนห้องพักรวมทั้งหมด:</span>
              {state.rooms.length > 0 ? (
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-800 font-mono font-bold text-xs">
                  {state.rooms.length} ห้อง
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-600 font-mono font-bold text-xs">
                  0 ห้อง (ยังไม่ได้สร้าง)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Card: Design and Pricing settings */}
        <div className="bg-white border border-slate-150 rounded-2xl p-5 space-y-4 shadow-sm">
          <h4 className="font-bold text-slate-700 text-sm flex items-center gap-2 border-b border-slate-50 pb-2">
            <Settings2 className="w-4.5 h-4.5 text-indigo-500" />
            ธีมและอัตราค่าน้ำค่าไฟ
          </h4>

          <div className="space-y-3 font-medium">
            <div className="flex justify-between items-center py-1">
              <span className="text-slate-400">สีธีมบิลที่เลือก:</span>
              <span className="flex items-center gap-1.5">
                <span 
                  className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                  style={{ backgroundColor: activeColorTheme.hex }}
                />
                <span className="text-slate-800 font-semibold">{activeColorTheme.name}</span>
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">การติดตั้งโลโก้:</span>
              <span className={`font-semibold ${state.logoUrl ? 'text-emerald-600' : 'text-slate-400'}`}>
                {state.logoUrl ? 'ติดตั้งโลโก้เรียบร้อย' : 'ใช้ชื่อย่อ (No Logo)'}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">{(!state.elecRateType || state.elecRateType === 'per_unit') ? 'ค่าไฟฟ้าต่อหน่วย:' : 'อัตราค่าไฟฟ้าเหมาจ่าย:'}</span>
              <span className="text-slate-800 font-bold">{state.elecRate} บาท / {(!state.elecRateType || state.elecRateType === 'per_unit') ? 'หน่วย' : 'ห้อง'}</span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">อัตราค่าน้ำประปา:</span>
              <span className="text-slate-800 font-semibold">{getWaterLabel()}</span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">ค่าส่วนกลางเริ่มต้น:</span>
              <span className="text-slate-800 font-semibold">
                {state.hasCommonFee ? `${Math.max(0, parseFloat(state.commonFeePrice) || 0)} บาท (${state.commonFeeType === 'per_person' ? 'รายคน' : 'รายห้อง'})` : 'ไม่ได้เปิดเก็บ'}
              </span>
            </div>

            <div className="flex justify-between items-center py-1 border-t border-slate-50">
              <span className="text-slate-400">ค่าบริการ Wi-Fi:</span>
              <span className="text-slate-800 font-semibold">
                {state.hasWifiFee ? `${Math.max(0, parseFloat(state.wifiFeePrice) || 0)} บาท (${state.wifiFeeType === 'per_person' ? 'รายคน' : 'รายห้อง'})` : 'ไม่ได้เปิดเก็บ'}
              </span>
            </div>
          </div>
        </div>

        {/* Full-width Card for Payment configuration */}
        <div className="bg-white border border-slate-150 rounded-2xl p-5 space-y-4 shadow-sm md:col-span-2">
          <h4 className="font-bold text-slate-700 text-sm flex items-center gap-2 border-b border-slate-50 pb-2">
            <CreditCard className="w-4.5 h-4.5 text-indigo-500" />
            ข้อมูลการรับชำระและโปรโมชั่น
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 font-medium">
            <div>
              <span className="text-slate-400 block mb-1">เลข PromptPay ที่ใช้รับเงิน:</span>
              <span className="text-slate-800 font-mono font-bold text-sm tracking-wide bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100 inline-block mt-0.5">
                {formattedPromptPay(state.promptPayNumber)}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block mb-1">ชื่อบัญชีผู้รับเงิน:</span>
              <span className="text-slate-800 font-bold text-sm bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100 inline-block mt-0.5">
                {state.accountName ? formattedAccountName(state.accountName) : <span className="text-rose-500 text-xs">ยังไม่ได้ระบุ</span>}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block mb-1">รหัสรับสิทธิ์พันธมิตร / คูปองโค้ด:</span>
              {state.promoCode ? (
                <span className="text-emerald-700 font-bold bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-100 inline-block mt-0.5">
                  {state.promoCode} (สิทธิ์พิเศษเปิดการงาน)
                </span>
              ) : (
                <span className="text-slate-400 font-normal italic inline-block mt-1.5">ไม่ได้ระบุรหัสโปรโมชั่น</span>
              )}
            </div>
          </div>
        </div>

      {/* Consent Checkbox */}
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm md:col-span-2">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            data-testid="step3-consent-checkbox"
            checked={state.step3Consent}
            onChange={(e) => onChange({ step3Consent: e.target.checked })}
            className="mt-1 w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500 cursor-pointer"
          />
          <div className="space-y-1">
            <span className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 transition">
              ข้าพเจ้าขอยืนยันว่าข้อมูลหอพักและข้อมูลการรับชำระเงินมีความถูกต้อง
            </span>
            <p className="text-xs text-slate-500 leading-relaxed">
              ข้าพเจ้ารับทราบว่าชื่อหอพัก โลโก้ และอัตราค่าบริการจะถูกเปิดเผยแก่ผู้เช่าของข้าพเจ้า และเข้าใจว่าระบบจะไม่บันทึกข้อมูลบัญชีรับเงินลงในเบราว์เซอร์เพื่อความปลอดภัยตามนโยบายความเป็นส่วนตัว ข้าพเจ้าพร้อมดำเนินการบันทึกข้อมูลเข้าสู่ระบบ
            </p>
          </div>
        </label>
        {errors.step3Consent && (
          <p className="text-xs text-rose-500 mt-3 flex items-center gap-1.5 ml-8 bg-rose-50 p-2 rounded-lg inline-flex">
            <AlertOctagon className="w-4 h-4" />
            {errors.step3Consent}
          </p>
        )}
      </div>

    </div>

    </div>
  );
};
